/**
 * Berlin Date Utilities - Single Source of Truth for Frontend
 *
 * ARCHITEKTUR-REGELN (VERBINDLICH):
 *
 * 1) Alle Datumsanzeigen in Europe/Berlin
 *    - Schichtdaten (shiftDate) sind UTC-Timestamps die 00:00 Berlin repräsentieren
 *    - Ereignisse (recordedAt/startedAt/endedAt) immer in Berlin anzeigen
 *
 * 2) VERBOTEN im Frontend:
 *    - toLocaleDateString() ohne timeZone: 'Europe/Berlin'
 *    - toISOString().split('T')[0]
 *    - Date-only aus ISO-String ableiten
 *
 * 3) Diese Utils nutzen:
 *    - formatBerlinDateISO: für YYYY-MM-DD (API, Vergleiche)
 *    - formatBerlinDateDE: für DD.MM.YYYY (Anzeige)
 *    - formatBerlinDateTimeDE: für DD.MM.YYYY, HH:mm (Ereignisse)
 */

const BERLIN_TZ = 'Europe/Berlin';

/**
 * Validiert und konvertiert Input zu Date
 * @returns Date oder null bei ungültigem Input
 */
function parseToDate(input: string | Date | null | undefined): Date | null {
  if (!input) return null;

  if (input instanceof Date) {
    return isNaN(input.getTime()) ? null : input;
  }

  // String: versuche zu parsen
  const date = new Date(input);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Formatiert ein Datum als YYYY-MM-DD in Berlin-Zeit
 *
 * Verwendung: API-Calls, Vergleiche, Keys
 *
 * @param input - ISO string oder Date
 * @returns "YYYY-MM-DD" in Berlin oder "—" bei ungültigem Input
 */
export function formatBerlinDateISO(input: string | Date | null | undefined): string {
  const date = parseToDate(input);
  if (!date) return '—';

  // sv-SE locale gibt YYYY-MM-DD Format
  return date.toLocaleDateString('sv-SE', { timeZone: BERLIN_TZ });
}

/**
 * Formatiert ein Datum als DD.MM.YYYY in Berlin-Zeit
 *
 * Verwendung: Deutsche Datumsanzeige im UI
 *
 * @param input - ISO string oder Date
 * @returns "DD.MM.YYYY" in Berlin oder "—" bei ungültigem Input
 */
export function formatBerlinDateDE(input: string | Date | null | undefined): string {
  const date = parseToDate(input);
  if (!date) return '—';

  return date.toLocaleDateString('de-DE', {
    timeZone: BERLIN_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Formatiert ein Datum mit Uhrzeit als DD.MM.YYYY, HH:mm in Berlin-Zeit
 *
 * Verwendung: Ereignis-Zeitpunkte (recordedAt, startedAt, endedAt, closedAt)
 *
 * @param input - ISO string oder Date
 * @returns "DD.MM.YYYY, HH:mm" in Berlin oder "—" bei ungültigem Input
 */
export function formatBerlinDateTimeDE(input: string | Date | null | undefined): string {
  const date = parseToDate(input);
  if (!date) return '—';

  return date.toLocaleString('de-DE', {
    timeZone: BERLIN_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Formatiert ein Datum mit Wochentag als "Montag, 01.01.2026" in Berlin-Zeit
 *
 * Verwendung: Ausführliche Datumsanzeige (z.B. Störungsdetail Header)
 *
 * @param input - ISO string oder Date
 * @returns "Wochentag, DD.MM.YYYY" in Berlin oder "—" bei ungültigem Input
 */
export function formatBerlinDateLongDE(input: string | Date | null | undefined): string {
  const date = parseToDate(input);
  if (!date) return '—';

  return date.toLocaleDateString('de-DE', {
    timeZone: BERLIN_TZ,
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}
